import { useEffect, useRef } from 'react'

/**
 * Custom hook for lightweight polling when a match is in PUBLISHING state
 * 
 * @param status - The current publishing status
 * @param onPoll - Callback function to execute on each poll (typically a mutate function)
 * @param interval - Polling interval in milliseconds (default: 2000ms)
 */
export const usePublishingPolling = (
  status: string | undefined,
  onPoll: () => void | Promise<void>,
  interval: number = 2000
) => {
  const intervalRef = useRef<number | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Only start polling if status is PUBLISHING
    if (status === 'PUBLISHING') {
      // Start polling
      intervalRef.current = window.setInterval(() => {
        if (isMountedRef.current) {
          onPoll()
        }
      }, interval)
    }

    // Cleanup function
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [status, onPoll, interval])
}

